import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { ItemStatusFilters } from "../../../types/admin";

interface ItemStatusFiltersPanelProps {
  filters: ItemStatusFilters;
  onFiltersChange: (filters: ItemStatusFilters) => void;
  selectedCount: number;
  onBulkActivate: () => void;
  onBulkDeactivate: () => void;
  onRefresh: () => void;
  onExport: () => void;
}

const ItemStatusFiltersPanel: React.FC<ItemStatusFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  selectedCount,
  onBulkActivate,
  onBulkDeactivate,
  onRefresh,
  onExport,
}) => {
  const { t } = useTranslation();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value,
      pageNumber: 1, // Reset to first page when searching
    });
  };

  const handleStatusFilterChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      isActive: value === "all" ? undefined : value === "active",
      pageNumber: 1,
    });
  };

  const handleSortChange = (event: any) => {
    onFiltersChange({
      ...filters,
      sortBy: event.target.value,
      pageNumber: 1,
    });
  };

  const handleSortDirectionChange = (event: any, value: string | null) => {
    if (value) {
      onFiltersChange({
        ...filters,
        sortDescending: value === "desc",
        pageNumber: 1,
      });
    }
  };

  const handlePageSizeChange = (event: any) => {
    onFiltersChange({
      ...filters,
      pageSize: parseInt(event.target.value),
      pageNumber: 1,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Search and Filters Row */}
      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          label={t("admin.itemStatusFilters.searchStatuses")}
          value={filters.search || ""}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 200 }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t("admin.itemStatusFilters.status")}</InputLabel>
          <Select
            value={
              filters.isActive === undefined
                ? "all"
                : filters.isActive
                  ? "active"
                  : "inactive"
            }
            onChange={handleStatusFilterChange}
            label={t("admin.itemStatusFilters.status")}
          >
            <MenuItem value="all">{t("admin.userFilters.all")}</MenuItem>
            <MenuItem value="active">{t("admin.userFilters.active")}</MenuItem>
            <MenuItem value="inactive">{t("admin.userFilters.inactive")}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t("admin.itemStatusFilters.sortBy")}</InputLabel>
          <Select
            value={filters.sortBy || "sortOrder"}
            onChange={handleSortChange}
            label={t("admin.itemStatusFilters.sortBy")}
          >
            <MenuItem value="sortOrder">{t("admin.itemStatusFilters.sortOrder")}</MenuItem>
            <MenuItem value="name">{t("admin.orderStatusForm.name")}</MenuItem>
            <MenuItem value="displayName">{t("admin.orderStatusForm.displayName")}</MenuItem>
            <MenuItem value="createdAt">{t("admin.itemStatusFilters.createdDate")}</MenuItem>
            <MenuItem value="updatedAt">{t("admin.itemStatusFilters.updatedDate")}</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={filters.sortDescending ? "desc" : "asc"}
          exclusive
          onChange={handleSortDirectionChange}
          size="small"
        >
          <ToggleButton value="asc">{t("admin.itemStatusFilters.asc")}</ToggleButton>
          <ToggleButton value="desc">{t("admin.itemStatusFilters.desc")}</ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>{t("admin.itemStatusFilters.perPage")}</InputLabel>
          <Select
            value={filters.pageSize || 20}
            onChange={handlePageSizeChange}
            label={t("admin.itemStatusFilters.perPage")}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Actions Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {selectedCount > 0 && (
            <>
              <Button
                variant="outlined"
                color="success"
                size="small"
                onClick={onBulkActivate}
              >
                {t("admin.itemStatusFilters.activate")} ({selectedCount})
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={onBulkDeactivate}
              >
                {t("admin.itemStatusFilters.deactivate")} ({selectedCount})
              </Button>
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            size="small"
          >
            {t("admin.itemStatusFilters.refresh")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onExport}
            size="small"
          >
            {t("admin.itemStatusFilters.export")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ItemStatusFiltersPanel;
